import { debounce } from "lodash-es";
import * as React from "react";
import { Link, useLocation } from "react-router-dom";

import { SvgSprite } from "@skv/core";

import { usePageEnvelope } from "@skv/state";

let storeCache = null;

const reducer = (
  state = {
    loading: false,
    error: false,
    data: null,
  },
  action
) => {
  switch (action.type) {
    case "ORG_DIRECTORY:FETCH:START":
      return {
        ...state,
        loading: true,
        error: false,
      };
    case "ORG_DIRECTORY:FETCH:END":
      return {
        ...state,
        loading: false,
        error: false,
        data: {
          ...state.data,
          [action.payload.data.directoryKey]: action.payload.data,
        },
      };

    default:
      return state;
  }
};

const createInitialStateFromPageEnvelope = (pageEnvelope, key) => {
  if (pageEnvelope.data && pageEnvelope.data[key]) {
    const result = pageEnvelope.data[key];

    return {
      loading: false,
      error: false,
      data: {
        [result.directoryKey]: result,
      },
    };
  }

  return {
    loading: true,
    error: false,
    data: null,
  };
};

const INITIAL_HEIGHT = 340;

const OrganisationDirectoryCollapse = (props) => {
  const { data } = props;

  const location = useLocation();
  const [isReady, setReady] = React.useState(false);
  const [height, setHeight] = React.useState(null);
  const [expanded, setExpanded] = React.useState(false);

  const handleViewAllClick = (e) => {
    setExpanded(!expanded);
  };

  const showViewAll = height > INITIAL_HEIGHT;

  const ulRef = React.useRef(null);

  React.useEffect(() => {
    const handleResize = () => {
      setHeight(ulRef.current.clientHeight);
    };

    const debouncedHandleResize = debounce(handleResize, 300);

    window.addEventListener("resize", debouncedHandleResize, false);

    handleResize();
    setReady(true);

    return () => {
      window.removeEventListener("resize", debouncedHandleResize, false);
    };
  }, []);

  React.useEffect(() => {
    setExpanded(false);
    setHeight(ulRef.current.clientHeight);
  }, [location.pathname, setExpanded]);

  return (
    <>
      {/* <pre>{height}</pre> */}
      <div className={isReady ? "is-ready" : ""}>
        <div
          className={expanded ? "expanded" : showViewAll && "collapsed"}
          style={
            isReady
              ? {
                  maxHeight: expanded ? height : INITIAL_HEIGHT,
                }
              : {}
          }
          css={`
            .is-ready && {
              overflow: hidden;
              position: relative;
              margin-bottom: 1rem;
              transition: max-height 0.3s ${(p) => p.theme.timingFnEaseOut};

              &.collapsed {
                &:after {
                  content: "";
                  position: absolute;
                  bottom: 0;
                  left: 0;
                  width: 100%;
                  height: 5rem;
                  background-image: linear-gradient(0deg, white, transparent);
                  pointer-events: none;
                }
              }
            }
          `}
        >
          <ul
            ref={ulRef}
            css={`
              margin-left: -0.5rem;
              margin-right: -0.5rem;

              @media (min-width: ${(p) => p.theme.smBreakpoint}) {
                column-count: 2;
              }

              @media (min-width: ${(p) => p.theme.mdBreakpoint}) {
                column-count: 3;
              }

              > li {
                padding-left: 0.5rem;
                padding-right: 0.5rem;
              }
            `}
            className="list-unstyled"
          >
            {data.organisations.map((org) => (
              <li key={org.id} className="mb-4">
                <Link
                  to={(loc) => ({
                    pathname: org.url,
                    state: {
                      referrer: loc,
                    },
                  })}
                >
                  {org.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        {showViewAll && (
          <button onClick={handleViewAllClick} className="btn btn-link ml-n3">
            <SvgSprite
              id="icons:chev-down"
              width={16}
              height={16}
              style={{
                transform: expanded
                  ? "rotate3d(1, 0, 0, 180deg)"
                  : "rotate3d(1, 0, 0, 0deg)",
              }}
              css={`
                transition: transform 0.3s ${(p) => p.theme.timingFnEaseOut};
              `}
            />{" "}
            View {expanded ? "less" : "all"}
          </button>
        )}
      </div>
    </>
  );
};

const OrganisationDirectoryResults: React.FC<{ letter: string }> = ({
  letter,
}) => {
  const { pageEnvelope } = usePageEnvelope();

  const [state, dispatch] = React.useReducer(
    reducer,
    storeCache ||
      createInitialStateFromPageEnvelope(pageEnvelope, "organisationDirectory")
  );

  const { data, error, loading } = state;
  console.log(state);

  React.useEffect(() => {
    // console.log(data);

    if (!data || !data[letter]) {
      dispatch({
        type: "ORG_DIRECTORY:FETCH:START",
        payload: {
          letter,
        },
      });

      fetch(`/api/organisation/directory/${letter}`)
        .then((res) => {
          if (res.ok) {
            return res.json();
          }
        })
        .then(({ data, errorCode, errorMessage, success }) => {
          if (success) {
            dispatch({
              type: "ORG_DIRECTORY:FETCH:END",
              payload: {
                data: data.organisationDirectory,
              },
            });
          }
          console.log(data);
        });
    }

    return () => {
      console.log("EXITING");
      storeCache = state;
    };
  }, [data, letter]);

  // console.log(state);

  return (
    <>
      <div className="mb-4">
        {((): React.ReactNode => {
          if (loading) {
            return <p className="mt-3">Please wait. Loading...</p>;
          }

          if (data[letter] && data[letter].totalCount) {
            return <OrganisationDirectoryCollapse data={data[letter]} />;
          }

          if (data[letter] && data[letter].totalCount === 0) {
            return <p>No organisations to display</p>;
          }

          return null;
        })()}
      </div>
    </>
  );
};

export default OrganisationDirectoryResults;
